import { useEffect } from 'react'

import { useWalletModal as useTronWalletModal } from '@tronweb3/tronwallet-adapter-react-ui'

import { analyticsService } from '@src/services/analytics'
import { createContextLogger } from '@src/utils/logger'

/**
 * Helper function to create a custom wallet row
 */
const createCustomWalletRow = (options: {
    className: string
    iconSrc: string
    iconAlt: string
    text: string
    onClick: () => void
}) => {
    const { className, iconSrc, iconAlt, text, onClick } = options

    // Create the row element
    const customRow = document.createElement('div')
    customRow.className = `MuiButtonBase-root MuiListItemButton-root MuiListItemButton-gutters MuiListItemButton-root MuiListItemButton-gutters css-lwhisp ${className}`
    customRow.setAttribute('tabindex', '0')
    customRow.setAttribute('role', 'button')
    customRow.style.borderRadius = '3px'

    // Add hover effect style if not already added
    if (!document.querySelector(`style[data-for="${className}"]`)) {
        const style = document.createElement('style')
        style.setAttribute('data-for', className)
        style.textContent = `
            .${className}:hover {
                background-color: rgba(255, 255, 255, 0.01);
                transition: background-color 0.2s ease;
            }
        `
        document.head.appendChild(style)
    }

    // Add custom content with styling matching other wallet buttons
    customRow.innerHTML = `
      <div style="display: flex; align-items: center;">
        <div class="MuiAvatar-root MuiAvatar-circular css-1dqisx" style="display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; margin-right: 16px;">
          <img alt="${iconAlt}" src="${iconSrc}" class="MuiAvatar-img css-45do71" style="width: 40px; height: 40px; object-fit: contain;">
        </div>
        <div class="MuiListItemText-root css-zfl4oa">
          <span class="MuiTypography-root MuiTypography-body1 MuiListItemText-primary css-fyswvn">${text}</span>
        </div>
      </div>
      <span class="MuiTouchRipple-root css-4mb1j7"></span>
    `

    // Add click handler
    customRow.addEventListener('click', onClick)

    return customRow
}

const logger = createContextLogger('WalletMenuModifier')

/**
 * Custom hook to inject additional content into the wallet menu dialog
 */
export const useWalletMenuModifier = () => {
    const { setVisible: openTronWalletModal } = useTronWalletModal()

    useEffect(() => {
        // Create a mutation observer to detect when the wallet menu dialog is rendered
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.addedNodes.length) {
                    // Check if the wallet menu dialog is rendered
                    const walletDialog = document.querySelector('.MuiDialog-paper')
                    if (walletDialog) {
                        // Find the wallet list
                        const walletList = walletDialog.querySelector('.MuiList-padding')
                        if (walletList) {
                            // Add Tron wallet row if it doesn't exist
                            if (!walletDialog.querySelector('.custom-tron-wallet-row')) {
                                const tronRow = createCustomWalletRow({
                                    className: 'custom-tron-wallet-row',
                                    iconSrc: '/assets/network/tron.png',
                                    iconAlt: 'Tron',
                                    text: 'Tron',
                                    onClick: () => {
                                        // Track the specific wallet connection attempt
                                        analyticsService.trackSpecificWalletConnectionAttempt('TronLink', 'TRX')
                                        logger.info('Tracked specific wallet connection attempt', { wallet: 'TronLink', chain: 'TRX' })
                                        
                                        openTronWalletModal(true)

                                        // Close the dialog
                                        const closeButton = document.querySelector('.MuiDialogTitle-root button')
                                        if (closeButton) {
                                            ;(closeButton as HTMLElement).click()
                                        }
                                    },
                                })

                                // Insert our custom row after the last wallet option
                                walletList.appendChild(tronRow)
                            }
                        }
                    }
                }
            })
        })

        // Start observing the entire document for changes
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        })
        
        // Use a more reliable approach to track wallet-specific connection attempts
        // Instead of manipulating DOM elements, we'll intercept click events at the document level
        const setupWalletTrackingInterceptor = () => {
            // Map wallet names to their types and chains
            const walletMapping: Record<string, { name: string, chain: string }> = {
                'MetaMask': { name: 'MetaMask', chain: 'EVM' },
                'WalletConnect': { name: 'WalletConnect', chain: 'EVM' },
                'Coinbase Wallet': { name: 'Coinbase', chain: 'EVM' },
                'Phantom': { name: 'Phantom', chain: 'SVM' },
                'Solflare': { name: 'Solflare', chain: 'SVM' },
                // Add more wallet mappings as needed
            }
            
            // Use event delegation to capture clicks on wallet buttons
            document.addEventListener('click', (event) => {
                // Only process if the wallet dialog is open
                const walletDialog = document.querySelector('.MuiDialog-paper')
                if (!walletDialog) return
                
                // Check if the click target is a wallet button or its child
                const target = event.target as HTMLElement
                const walletButton = target.closest('.MuiListItemButton-root') as HTMLElement
                
                if (walletButton && walletDialog.contains(walletButton)) {
                    // Get the button text
                    const buttonText = walletButton.textContent?.trim() || ''
                    
                    // Find the wallet in our mapping
                    for (const [key, info] of Object.entries(walletMapping)) {
                        if (buttonText.includes(key)) {
                            // This is a wallet button click - track it!
                            logger.info('Intercepted wallet button click', { wallet: info.name, chain: info.chain })
                            
                            // Track the specific wallet connection attempt
                            // We use setTimeout with 0ms to ensure this runs after the current event loop
                            // This helps ensure the event is tracked even if there are errors in the connection process
                            setTimeout(() => {
                                analyticsService.trackSpecificWalletConnectionAttempt(info.name, info.chain)
                                logger.info('Tracked specific wallet connection attempt', { wallet: info.name, chain: info.chain })
                            }, 0)
                            
                            break
                        }
                    }
                }
            }, { capture: true }) // Use capture to ensure we get the event before any stopPropagation calls
        }
        
        // Set up the wallet tracking interceptor
        setupWalletTrackingInterceptor()

        // Function to check for and modify the Tron buttons in the wallet drawer
        const checkAndModifyTronButtons = () => {
            const portfolioDrawer = document.querySelector('[class*="WalletPortfolioDrawer"]')
            if (!portfolioDrawer) return

            // Find all buttons with "Tron" text in the drawer
            const tronButtons = Array.from(portfolioDrawer.querySelectorAll('button')).filter(button =>
                button.textContent?.includes('Tron')
            )

            // Add our click handler to each Tron button
            tronButtons.forEach(button => {
                // Remove existing click handlers by cloning and replacing the button
                const newButton = button.cloneNode(true) as HTMLButtonElement

                // Add our custom click handler
                newButton.onclick = e => {
                    e.preventDefault()
                    e.stopPropagation()

                    openTronWalletModal(true)

                    // Call the original click handler to maintain filter functionality
                    setTimeout(() => {
                        const event = new MouseEvent('click', {
                            bubbles: true,
                            cancelable: true,
                            view: window,
                        })
                        button.dispatchEvent(event)
                    }, 10)

                    return false
                }

                // Replace the original button
                if (button.parentNode) {
                    button.parentNode.replaceChild(newButton, button)
                }
            })
        }

        // Set an interval to periodically check for Tron buttons in the drawer
        // This is more reliable than mutation observers for complex React components
        const intervalId = setInterval(checkAndModifyTronButtons, 1000)

        // Clean up
        return () => {
            observer.disconnect()
            clearInterval(intervalId)
        }
    }, [openTronWalletModal])
}
